"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCombobox = void 0;
/* eslint-disable sonarjs/cognitive-complexity */
const react_1 = require("react");
const hooks_1 = require("../utils/hooks");
const useCombobox = (ref, items, selectOnly, phoneValues) => {
    const [isOpen, setOpen] = (0, react_1.useState)(false);
    const [phoneValue, setPhoneValue] = (0, react_1.useState)(phoneValues);
    const [selectedIndex, setSelectedIndex] = (0, react_1.useState)(undefined);
    const comboboxRef = (0, react_1.useRef)(null);
    const listboxRef = (0, react_1.useRef)(null);
    const listitemRef = (0, react_1.useRef)([]);
    const filteredOptions = (0, react_1.useMemo)(() => {
        if (selectedIndex !== undefined || selectOnly) {
            return items;
        }
        return items.filter((item) => {
            var _a;
            return item.value
                .toString()
                .toLowerCase()
                .includes(((_a = phoneValue === null || phoneValue === void 0 ? void 0 : phoneValue.phone) === null || _a === void 0 ? void 0 : _a.trim().toLowerCase()) || '');
        });
    }, [phoneValue === null || phoneValue === void 0 ? void 0 : phoneValue.phone, selectedIndex]);
    (0, react_1.useEffect)(() => {
        var _a, _b;
        const focusedElement = listitemRef.current.find((element) => {
            return element === null || element === void 0 ? void 0 : element.classList.contains('-focused');
        });
        focusedElement === null || focusedElement === void 0 ? void 0 : focusedElement.classList.remove('-focused');
        if (selectedIndex !== undefined) {
            const listitem = ((_a = listitemRef.current[selectedIndex]) === null || _a === void 0 ? void 0 : _a.getAttribute('value')) || '';
            // setValue(listitemRef.current[selectedIndex]?.getAttribute('value') || '');
            setPhoneValue((prev) => (Object.assign(Object.assign({}, prev), { ddi: listitem })));
            (_b = listitemRef.current[selectedIndex]) === null || _b === void 0 ? void 0 : _b.classList.add('-focused');
        }
    }, [selectedIndex]);
    (0, react_1.useEffect)(() => {
        if (!isOpen) {
            setSelectedIndex(undefined);
        }
    }, [isOpen]);
    const onItemClick = (nextValue) => {
        // setValue(nextValue);
        setPhoneValue((prev) => (Object.assign(Object.assign({}, prev), { ddi: nextValue })));
        setOpen(false);
    };
    const onComboboxBlur = () => {
        setOpen(false);
    };
    const onComboboxChange = (event) => {
        var _a, _b;
        setOpen(true);
        const parsedEvent = event;
        if (((_b = (_a = parsedEvent === null || parsedEvent === void 0 ? void 0 : parsedEvent.nativeEvent) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.length) === 1) {
            setSelectedIndex(undefined);
        }
    };
    const onComboboxClick = () => {
        setOpen(!isOpen);
    };
    const returnNextValidItem = (reverse = false) => {
        var _a;
        if (selectedIndex !== undefined) {
            const nextIndex = reverse ? selectedIndex - 1 : selectedIndex + 1;
            if (filteredOptions.length > 1 && listitemRef.current[nextIndex]) {
                const disabledAttr = 'aria-disabled';
                if (((_a = listitemRef.current[nextIndex]) === null || _a === void 0 ? void 0 : _a.getAttribute(disabledAttr)) === 'true') {
                    const nextValidItem = reverse
                        ? listitemRef.current
                            .slice(0, nextIndex)
                            .reverse()
                            .find((element) => (element === null || element === void 0 ? void 0 : element.getAttribute(disabledAttr)) === 'false')
                        : listitemRef.current
                            .slice(nextIndex)
                            .find((element) => (element === null || element === void 0 ? void 0 : element.getAttribute(disabledAttr)) === 'false');
                    return listitemRef.current.findIndex((element) => element === nextValidItem);
                }
                return nextIndex;
            }
        }
        // if there is a value, another step is added due to filtredOptions
        if (phoneValue && (phoneValue === null || phoneValue === void 0 ? void 0 : phoneValue.phone) !== '') {
            const itemIndex = items.findIndex((option) => option.value
                .toString()
                .toLowerCase()
                .includes(phoneValue.phone.trim().toLowerCase()));
            const nextValidItem = reverse
                ? items
                    .slice(0, itemIndex)
                    .reverse()
                    .find((item) => !(item === null || item === void 0 ? void 0 : item.disabled))
                : items.slice(itemIndex + 1).find((item) => !(item === null || item === void 0 ? void 0 : item.disabled));
            const nextValidItemIndex = items.findIndex((element) => element === nextValidItem);
            if (nextValidItemIndex === -1) {
                return reverse ? filteredOptions.length - 1 : 0;
            }
            return nextValidItemIndex;
        }
        if (reverse) {
            const lastValidItem = listitemRef.current
                .slice(0)
                .reverse()
                .find((element) => !(element === null || element === void 0 ? void 0 : element.ariaDisabled));
            const lastValidItemIndex = listitemRef.current.findIndex((element) => element === lastValidItem);
            return lastValidItemIndex === -1
                ? filteredOptions.length - 1
                : lastValidItemIndex;
        }
        const nextValidElementIndex = listitemRef.current.findIndex((element) => !(element === null || element === void 0 ? void 0 : element.ariaDisabled));
        return nextValidElementIndex === -1 ? 0 : nextValidElementIndex;
    };
    const onComboboxKeyDown = (event) => {
        var _a, _b, _c;
        const altKey = event.altKey;
        if (event.ctrlKey || event.shiftKey) {
            return;
        }
        switch (event.key) {
            case 'Enter':
                if (selectedIndex !== undefined) {
                    (_a = listitemRef.current[selectedIndex]) === null || _a === void 0 ? void 0 : _a.click();
                }
                break;
            case 'Down':
            case 'ArrowDown':
                if (filteredOptions.length > 0 && !altKey) {
                    setOpen(true);
                    setSelectedIndex(returnNextValidItem());
                }
                break;
            case 'Up':
            case 'ArrowUp':
                if (filteredOptions.length > 0) {
                    setOpen(true);
                    setSelectedIndex(returnNextValidItem(true));
                }
                break;
            case 'Esc':
            case 'Escape':
                setOpen(false);
                if ((phoneValue === null || phoneValue === void 0 ? void 0 : phoneValue.phone) === '') {
                    setSelectedIndex(undefined);
                    setPhoneValue({
                        ddi: '',
                        phone: '',
                    });
                }
                break;
            case 'Home':
                (_b = comboboxRef.current) === null || _b === void 0 ? void 0 : _b.setSelectionRange(0, 0);
                break;
            case 'End':
                // eslint-disable-next-line no-case-declarations
                const length = (phoneValue === null || phoneValue === void 0 ? void 0 : phoneValue.phone) ? phoneValue.phone.length : 0;
                (_c = comboboxRef.current) === null || _c === void 0 ? void 0 : _c.setSelectionRange(length, length);
                break;
            default:
                break;
        }
    };
    const onPhoneChange = (text) => {
        setPhoneValue((prev) => (Object.assign(Object.assign({}, prev), { phone: text })));
    };
    const clearValue = () => {
        setPhoneValue({ phone: '', ddi: '' });
    };
    const { maskValue, maskRef, onMaskChange } = (0, hooks_1.useMask)('(99) 99999 9999', phoneValue.phone, ref, onPhoneChange);
    return {
        phoneValue,
        isOpen,
        filteredOptions,
        listitemRef,
        listboxRef,
        comboboxRef,
        onComboboxClick,
        onComboboxKeyDown,
        onComboboxChange,
        onComboboxBlur,
        onItemClick,
        maskRef,
        maskValue,
        onMaskChange,
        clearValue,
    };
};
exports.useCombobox = useCombobox;
//# sourceMappingURL=useCombobox.js.map